import { useEffect, useState, type FormEvent } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";

interface DriveStatus {
  client_id: string;
  client_secret_set: boolean;
  refresh_token_set: boolean;
  folder_id: string;
}

export default function AdminGoogleDrive() {
  const [status, setStatus] = useState<DriveStatus | null>(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [folderId, setFolderId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [newFolderName, setNewFolderName] = useState("Espacio Borman - Fotos");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderMessage, setFolderMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/integrations/google-drive`);
    const json: DriveStatus = await res.json();
    setStatus(json);
    setClientId(json.client_id);
    setFolderId(json.folder_id);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const payload: Record<string, string> = { client_id: clientId, folder_id: folderId };
    if (clientSecret) payload.client_secret = clientSecret;
    if (refreshToken) payload.refresh_token = refreshToken;

    try {
      const res = await fetch(`${API_BASE}/integrations/google-drive`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setMessage({ type: "error", text: json?.error || "No se pudo guardar" });
        return;
      }
      setStatus(json);
      setClientSecret("");
      setRefreshToken("");
      setMessage({ type: "success", text: "Credenciales guardadas." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/integrations/google-drive/test`, { method: "POST" });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.ok) {
        const folderText = json.folderName ? ` — carpeta "${json.folderName}" encontrada` : "";
        setTestResult({ ok: true, text: `Conectado correctamente como ${json.email}${folderText}.` });
      } else {
        setTestResult({ ok: false, text: json?.error || "No se pudo conectar con Google Drive." });
      }
    } catch {
      setTestResult({ ok: false, text: "Error de conexión con el servidor." });
    } finally {
      setTesting(false);
    }
  }

  async function handleCreateFolder() {
    setCreatingFolder(true);
    setFolderMessage(null);
    try {
      const res = await fetch(`${API_BASE}/integrations/google-drive/folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newFolderName }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        setFolderMessage({ ok: false, text: json?.error || "No se pudo crear la carpeta." });
        return;
      }
      const saveRes = await fetch(`${API_BASE}/integrations/google-drive`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, folder_id: json.id }),
      });
      const saveJson = await saveRes.json().catch(() => null);
      if (saveRes.ok) {
        setStatus(saveJson);
        setFolderId(json.id);
      }
      setFolderMessage({ ok: true, text: `Carpeta "${json.name}" creada y guardada como carpeta de destino.` });
    } catch {
      setFolderMessage({ ok: false, text: "Error de conexión con el servidor." });
    } finally {
      setCreatingFolder(false);
    }
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm";
  const labelCls = "block text-sm font-medium text-foreground-700 mb-1.5";

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Google Drive</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Credenciales de la app de Google (OAuth Client ID) para subir imágenes a Google Drive. Se guardan acá, no en
        el servidor.
      </p>

      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <span className={status?.client_secret_set ? "text-primary-700" : "text-foreground-400"}>
          {status?.client_secret_set ? "✓" : "○"} Client Secret {status?.client_secret_set ? "configurado" : "no configurado"}
        </span>
        <span className={status?.refresh_token_set ? "text-primary-700" : "text-foreground-400"}>
          {status?.refresh_token_set ? "✓" : "○"} Refresh Token {status?.refresh_token_set ? "configurado" : "no configurado"}
        </span>
      </div>

      <div className="mt-4 max-w-lg">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing}
          className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
        >
          {testing ? "Probando conexión..." : "Probar conexión con Google Drive"}
        </button>
        <p className="mt-1 text-xs text-foreground-500">
          Guardado no significa que funcione: esto hace una llamada real a Google para confirmarlo.
        </p>
        {testResult && (
          <div
            className={`mt-2 rounded-md p-3 text-sm border ${
              testResult.ok ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
            }`}
          >
            {testResult.ok ? "✓ " : "✗ "}
            {testResult.text}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4 max-w-lg">
        {message && (
          <div
            className={`rounded-md p-3 text-sm border ${
              message.type === "success" ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <div>
          <label className={labelCls}>Client ID</label>
          <input className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Client Secret</label>
          <input
            type="password"
            className={inputCls}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={status?.client_secret_set ? "•••••••• (ya configurado, deja en blanco para no cambiarlo)" : "Pega tu Client Secret"}
          />
        </div>

        <div>
          <label className={labelCls}>Refresh Token</label>
          <input
            type="password"
            className={inputCls}
            value={refreshToken}
            onChange={(e) => setRefreshToken(e.target.value)}
            placeholder={status?.refresh_token_set ? "•••••••• (ya configurado, deja en blanco para no cambiarlo)" : "Pega tu Refresh Token"}
          />
        </div>

        <div>
          <label className={labelCls}>ID de carpeta en Drive (opcional)</label>
          <input className={inputCls} value={folderId} onChange={(e) => setFolderId(e.target.value)} placeholder="Déjalo vacío para subir a la raíz de tu Drive" />
          <p className="mt-1 text-xs text-foreground-500">
            Una carpeta que ya tenías creada a mano en tu Drive no va a funcionar (Google no le da acceso a la app).
            Crea una carpeta nueva desde acá para que la app sí pueda usarla:
          </p>
          <div className="mt-2 flex gap-2">
            <input
              className={inputCls}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta nueva"
            />
            <button
              type="button"
              onClick={handleCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
            >
              {creatingFolder ? "Creando..." : "Crear carpeta"}
            </button>
          </div>
          {folderMessage && (
            <div
              className={`mt-2 rounded-md p-3 text-sm border ${
                folderMessage.ok ? "bg-primary-100 border-primary-200 text-primary-800" : "bg-accent-100 border-accent-200 text-accent-800"
              }`}
            >
              {folderMessage.ok ? "✓ " : "✗ "}
              {folderMessage.text}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {saving ? "Guardando..." : "Guardar credenciales"}
        </button>
      </form>
    </AdminLayout>
  );
}
